import { useEffect } from "react";

/**
 * Adds `<meta name="robots" content="noindex, nofollow, noarchive">` (plus the
 * googlebot/AI-friendly variants) while an admin screen is mounted, and removes them
 * on unmount. Belt-and-suspenders alongside robots.txt: keeps the admin panel out of
 * search engines and AI crawlers even though it lives in a client-side SPA route.
 */
const ROBOTS_CONTENT = "noindex, nofollow, noarchive, nosnippet, noimageindex";
const ROBOTS_NAMES = ["robots", "googlebot", "GPTBot", "ClaudeBot"];

export function useNoIndex(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const created: HTMLMetaElement[] = [];
    const touched: Array<{ el: HTMLMetaElement; prev: string | null }> = [];

    for (const name of ROBOTS_NAMES) {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (el) {
        touched.push({ el, prev: el.getAttribute("content") });
        el.setAttribute("content", ROBOTS_CONTENT);
      } else {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        el.setAttribute("content", ROBOTS_CONTENT);
        document.head.appendChild(el);
        created.push(el);
      }
    }

    return () => {
      for (const el of created) el.remove();
      for (const { el, prev } of touched) {
        if (prev === null) el.removeAttribute("content");
        else el.setAttribute("content", prev);
      }
    };
  }, [enabled]);
}
